import { InjectionToken } from '@angular/core';
import { FHIRItemType } from '../models/fhir.model';

/**
 * Control Component Metadata
 *
 * Abstraction for control metadata needed by FHIR converters. This allows conductor to remain independent of
 * @complexcare/stage.
 */
export interface IControlComponentMetadata {
  /** Unique control type identifier (e.g., 'text-input-v1') */
  controlType: string;

  /** Display label for the control */
  label: string;

  /** Component class name */
  componentType?: string;

  /** Package name */
  package?: string;

  /** Category for palette grouping */
  category?: string;

  /** Whether this is a layout/container control */
  isLayoutControl?: boolean;

  /** FHIR item type mapping */
  fhirType?: FHIRItemType | string;

  /** Whether control supports answer options */
  supportsOptions?: boolean;

  /** Property definitions for the properties panel */
  propertyDefinitions?: IPropertyDefinition[];
}

/** Property definition for a control (UI properties panel) */
export interface IPropertyDefinition {
  /** Property ID (matches the control's property name) */
  id: string;

  /** Human-readable label */
  label: string;

  /** Property type */
  type: 'text' | 'textarea' | 'number' | 'boolean' | 'select' | 'custom' | string;

  /** Property group for organization */
  group?: 'basic' | 'specific' | 'validation' | 'fhir' | 'advanced' | string;

  /** Default value */
  defaultValue?: unknown;

  /** Placeholder text */
  placeholder?: string;

  /** Help text or description */
  helpText?: string;

  /** Is this property required? */
  required?: boolean;

  /** Options for select type */
  options?: Array<{ label: string; value: unknown }>;

  /** Custom component identifier for custom type */
  customComponent?: string;

  /** Visibility condition (may be a function in some implementations) */
  visibleWhen?: unknown;
}

/**
 * Palette Control Item
 *
 * Minimal control info for palette data lookup.
 */
export interface IPaletteControlItem {
  controlType: string;
  label?: string;
}

/**
 * Control Metadata Provider Interface
 *
 * Abstraction for looking up control component metadata. Implementations should use @complexcare/stage's
 * ControlComponentFactory.
 */
export interface IControlMetadataProvider {
  /**
   * Get metadata for a specific control type
   *
   * @param controlType - The control type identifier
   * @returns Metadata or null if not found
   */
  getMetadata(controlType: string): IControlComponentMetadata | null;

  /**
   * Get palette data for a category
   *
   * @param category - Optional category filter
   * @returns Array of palette items
   */
  getPaletteData(category?: string): IPaletteControlItem[] | null;
}

/** Injection token for control metadata provider */
export const CONTROL_METADATA_PROVIDER = new InjectionToken<IControlMetadataProvider>('CONTROL_METADATA_PROVIDER');
